import { z } from "zod";

const validEmailText = "Please enter valid email address";
const passwordRegexFailedText =
  "Password must contain at least 1 uppercase letter, 1 number, and 1 special character";
const passwordLengthText = "Password must be at least 8 characters long";
const emailToShort = "email address is too short";
const firstNameShort = "First name is too short";
const firstNameLong = "First name is too long";
const lastNameShort = "Last name is too short";
const lastNameLong = "Last name is too long";

const loginSchema = z.object({
  email: z
    .string()
    .nonempty("Email is required")
    .email(validEmailText)
    .min(5, emailToShort)
    .max(50, "email address is too long"),
  password: z
    .string()
    .nonempty("Password is required")
    .min(8, passwordLengthText)
    .regex(/^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).+$/, passwordRegexFailedText),
});

const registerSchema = z.object({
  firstName: z
    .string()
    .nonempty("First name is required")
    .regex(/^[A-Za-z0-9 ]+$/, "Invalid first name")
    .min(2, firstNameShort)
    .max(50, firstNameLong),
  lastName: z
    .string()
    .nonempty("Last name is required")
    .regex(/^[A-Za-z0-9 ]+$/, "Invalid last name")
    .min(2, lastNameShort)
    .max(50, lastNameLong),
  email: z
    .string()
    .email("Invalid email address")
    .nonempty("Email is required")
    .email(validEmailText)
    .min(5, emailToShort)
    .max(150, "email address is too long"),
  password: z
    .string()
    .nonempty("Password is required")
    .min(8, passwordLengthText)
    .regex(/^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).+$/, passwordRegexFailedText),
});

const forgotPasswordSchema = z
  .object({
    email: z
      .string()
      .nonempty("Email is required")
      .email(validEmailText)
      .min(5, emailToShort)
      .max(50, "email address is too long"),
    password: z
      .string()
      .nonempty("Password is required")
      .min(8, passwordLengthText)
      .regex(/^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).+$/, passwordRegexFailedText),
    repeatPassword: z.string(),
  })
  .refine((data) => data.password === data.repeatPassword, {
    message: "Passwords must match",
    path: ["repeatPassword"],
  });

const profileUpdateSchema = z.object({
  firstName: z
    .string()
    .nonempty("First name is required")
    .regex(/^[A-Za-z0-9 ]+$/, "Invalid first name")
    .min(2, firstNameShort)
    .max(50, firstNameLong),
  lastName: z
    .string()
    .nonempty("Last name is required")
    .regex(/^[A-Za-z0-9 ]+$/, "Invalid last name")
    .min(2, lastNameShort)
    .max(50, lastNameLong),
  phoneNumber: z.preprocess(
    (val) => {
      if (typeof val !== "string" || val.trim() === "") return undefined;
      return val.trim();
    },
    z
      .string()
      .regex(/^\+?[1-9]\d{6,14}$/, "Invalid phone number format")
      .optional()
  ),
  dob: z.preprocess(
    (val) => {
      if (val === "" || val === null || val === undefined) return null;
      return typeof val === "string" ? new Date(val) : val;
    },
    z
      .date({ invalid_type_error: "Invalid date format (YYYY-MM-DD required)" })
      .refine((date) => !isNaN(date.getTime()), { message: "Invalid date" })
      .nullable() // Allow null values explicitly
  ),
  profilePic: z.preprocess(
    (val) =>
      val === "" || val === null || val === undefined ? undefined : val,
    z.string().optional()
  ),
});

const usersProfileUpdateSchema = z.object({
  firstName: z
    .string()
    .nonempty("First name is required")
    .regex(/^[A-Za-z0-9 ]+$/, "Invalid first name")
    .min(2, firstNameShort)
    .max(50, firstNameLong),
  lastName: z
    .string()
    .nonempty("Last name is required")
    .regex(/^[A-Za-z0-9 ]+$/, "Invalid last name")
    .min(2, lastNameShort)
    .max(50, lastNameLong),
  phoneNumber: z.preprocess(
    (val) =>
      val === "" || val === null || val === undefined ? undefined : val,
    z
      .string()
      .regex(/^\+?[1-9]\d{1,14}$/, "Invalid phone number format")
      .optional()
  ),
  dob: z.preprocess(
    (val) => {
      if (val === "" || val === null || val === undefined) return null;
      return typeof val === "string" ? new Date(val) : val;
    },
    z
      .date({ invalid_type_error: "Invalid date format (YYYY-MM-DD required)" })
      .refine((date) => !isNaN(date.getTime()), { message: "Invalid date" })
      .nullable() // Allow null values explicitly
  ),
  profilePic: z.preprocess(
    (val) =>
      val === "" || val === null || val === undefined ? undefined : val,
    z.string().optional()
  ),
});

export {
  loginSchema,
  registerSchema,
  forgotPasswordSchema,
  profileUpdateSchema,
  usersProfileUpdateSchema,
};
